import { useTranslation } from 'react-i18next';
import { ChevronDown } from 'lucide-react';

import { NavigationItem } from './navigation-item';
import { SidebarHeader } from './sidebar-header';
import { SidebarFooter } from './sidebar-footer';
import { SidebarToggle } from '@/shared/ui/sidebar-toggle';
import { cn } from '@/lib/utils';
import type { NavigationItemWithState } from '@/entities/navigation';
import type { User } from '@/entities/layout';

interface DesktopSidebarProps {
  user?: User;
  projectName: string;
  siteName: string;
  instanceName: string;
  isCluster: boolean;
  isCollapsed: boolean;
  items: NavigationItemWithState[];
  onToggleItem: (code: string) => void;
  onToggleSidebar: () => void;
}

export const DesktopSidebar = ({
  user,
  projectName,
  siteName,
  instanceName,
  isCluster,
  isCollapsed,
  items,
  onToggleItem,
  onToggleSidebar
}: DesktopSidebarProps) => {
  const { t } = useTranslation();

  return (
    <aside
      className={cn(
        'relative flex flex-col h-screen bg-background border-r transition-all duration-300 shrink-0',
        isCollapsed ? 'w-16' : 'w-64'
      )}>
      <SidebarHeader
        projectName={projectName}
        siteName={siteName}
        instanceName={instanceName}
        isCluster={isCluster}
        isMobile={false}
        isCollapsed={isCollapsed}
        closeSidebar={() => {}}
      />

      <div className="flex-1 overflow-y-auto py-4 custom-scrollbar">
        <div className="mb-6 px-3">
          {!isCollapsed && (
            <h3 className="text-xs font-medium text-muted-foreground mb-2 px-2 flex items-center justify-between group cursor-pointer">
              {t('sidebar.menu')}
              <ChevronDown className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
            </h3>
          )}
          <ul className="space-y-1">
            {items.map((item) => (
              <li key={item.code}>
                <NavigationItem item={item} isCollapsed={isCollapsed} onToggle={onToggleItem} />
              </li>
            ))}
          </ul>
        </div>
      </div>

      <SidebarFooter user={user} isCollapsed={isCollapsed} isMobile={false} />
      <SidebarToggle isCollapsed={isCollapsed} onToggle={onToggleSidebar} />
    </aside>
  );
};
